import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { FiUser, FiMail, FiPhone, FiEdit2, FiSave, FiX, FiUpload, FiCamera, FiCheckCircle, FiXCircle, FiShield } from 'react-icons/fi';
import { Card, Button, Input, Loading, AlertModal } from '../components/common';
import { getUserById, updateUserProfile } from '../firebase/users';
import { uploadUserDocument } from '../firebase/storage';
import Navbar from '../components/Navbar';
import { useAuth } from '../context/AuthContext';

const Profile = () => {
  const navigate = useNavigate();
  const { user: authUser, userProfile, refreshUserProfile } = useAuth();
  const [user, setUser] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [formData, setFormData] = useState({
    displayName: '',
    phone: '',
    email: '',
    aadharNumber: '',
    alternatePhone: '',
    whatsappNumber: '',
    address: ''
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(true);
  const [uploadingDoc, setUploadingDoc] = useState(null);
  const [alert, setAlert] = useState({ isOpen: false, type: 'success', title: '', message: '' });
  
  // Document refs
  const licenseFrontRef = useRef(null);
  const licenseBackRef = useRef(null);
  const aadharFrontRef = useRef(null);

  useEffect(() => {
    const fetchUserProfile = async () => {
      try {
        if (!authUser || !authUser.uid) {
          setPageLoading(false);
          navigate('/login');
          return;
        }

        // Use userProfile from context if available, otherwise fetch
        if (userProfile) {
          setUser(userProfile);
          setFormData({
            displayName: userProfile.displayName || userProfile.name || 'User',
            phone: userProfile.phone || '',
            email: userProfile.email || authUser.email || '',
            aadharNumber: userProfile.aadharNumber || '',
            alternatePhone: userProfile.alternatePhone || '',
            whatsappNumber: userProfile.whatsappNumber || '',
            address: userProfile.address || ''
          });
          setPageLoading(false);
        } else {
          // Fetch from Firestore
          const result = await getUserById(authUser.uid);
          if (result.user) {
            setUser(result.user);
            setFormData({
              displayName: result.user.displayName || result.user.name || 'User',
              phone: result.user.phone || '',
              email: result.user.email || authUser.email || '',
              aadharNumber: result.user.aadharNumber || '',
              alternatePhone: result.user.alternatePhone || '',
              whatsappNumber: result.user.whatsappNumber || '',
              address: result.user.address || ''
            });
          } else {
            // Fallback to auth user
            setUser({
              ...authUser,
              displayName: authUser.displayName || authUser.email?.split('@')[0] || 'User',
              phone: '',
              email: authUser.email || ''
            });
            setFormData({
              displayName: authUser.displayName || authUser.email?.split('@')[0] || 'User',
              phone: '',
              email: authUser.email || '',
              aadharNumber: '',
              alternatePhone: '',
              whatsappNumber: '',
              address: ''
            });
          }
          setPageLoading(false);
        }
      } catch (error) {
        console.error('Error loading profile:', error);
        setPageLoading(false);
        navigate('/login');
      }
    };

    fetchUserProfile();
  }, [authUser, userProfile, navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const validate = () => {
    const newErrors = {};
    const trimmedName = formData.displayName?.trim() || '';
    if (!trimmedName || trimmedName.length < 2) {
      newErrors.displayName = 'Name must be at least 2 characters';
    }
    
    // Phone is now required
    const phoneTrimmed = formData.phone?.trim() || '';
    if (!phoneTrimmed) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^[0-9]{10}$/.test(phoneTrimmed)) {
      newErrors.phone = 'Phone number must be 10 digits';
    }
    
    // Aadhar Number is required
    const aadharTrimmed = formData.aadharNumber?.trim() || '';
    if (!aadharTrimmed) {
      newErrors.aadharNumber = 'Aadhar number is required';
    } else {
      // Remove spaces and check if it's 12 digits
      const aadharDigits = aadharTrimmed.replace(/\s/g, '');
      if (!/^[0-9]{12}$/.test(aadharDigits)) {
        newErrors.aadharNumber = 'Aadhar number must be 12 digits';
      }
    }
    
    // Address is required
    const addressTrimmed = formData.address?.trim() || '';
    if (!addressTrimmed) {
      newErrors.address = 'Address is required';
    } else if (addressTrimmed.length < 10) {
      newErrors.address = 'Address must be at least 10 characters';
    }
    
    // Validate alternate phone if provided
    if (formData.alternatePhone && formData.alternatePhone.trim()) {
      const altPhoneTrimmed = formData.alternatePhone.trim();
      if (!/^[0-9]{10}$/.test(altPhoneTrimmed)) {
        newErrors.alternatePhone = 'Alternate phone must be 10 digits';
      }
    }
    
    // Validate WhatsApp if provided
    if (formData.whatsappNumber && formData.whatsappNumber.trim()) {
      const whatsappTrimmed = formData.whatsappNumber.trim();
      if (!/^[0-9]{10}$/.test(whatsappTrimmed)) {
        newErrors.whatsappNumber = 'WhatsApp number must be 10 digits';
      }
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate()) return;

    setLoading(true);
    
    // Always update localStorage first
    const updatedUser = {
      ...user,
      displayName: formData.displayName,
      phone: formData.phone,
      aadharNumber: formData.aadharNumber,
      alternatePhone: formData.alternatePhone,
      whatsappNumber: formData.whatsappNumber,
      address: formData.address
    };
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setUser(updatedUser);
    
    // Update Firestore
    if (user?.id || user?.uid) {
      try {
        const userId = user.id || user.uid;
        const { error } = await updateUserProfile(userId, {
          displayName: formData.displayName,
          phone: formData.phone,
          aadharNumber: formData.aadharNumber,
          alternatePhone: formData.alternatePhone,
          whatsappNumber: formData.whatsappNumber,
          address: formData.address
        });

        if (error) {
          console.error('Firestore update error:', error);
          setAlert({
            isOpen: true,
            type: 'error',
            title: 'Update Failed',
            message: 'Failed to update profile. Please try again.'
          });
        } else {
          // Refresh user profile
          if (refreshUserProfile) {
            await refreshUserProfile();
          }
          // Reload user data
          const result = await getUserById(userId);
          if (result.user) {
            setUser(result.user);
          }
        }
      } catch (error) {
        console.error('Error updating profile:', error);
        setAlert({
          isOpen: true,
          type: 'error',
          title: 'Update Failed',
          message: 'Failed to update profile. Please try again.'
        });
      }
    }

    setIsEditing(false);
    setLoading(false);
  };

  const handleCancel = () => {
    setFormData({
      displayName: user.displayName || '',
      phone: user.phone || '',
      email: user.email || '',
      aadharNumber: user.aadharNumber || '',
      alternatePhone: user.alternatePhone || '',
      whatsappNumber: user.whatsappNumber || '',
      address: user.address || ''
    });
    setErrors({});
    setIsEditing(false);
  };

  // Handle document upload
  const handleDocumentUpload = async (documentType, file) => {
    if (!file) {
      console.error('No file provided to handleDocumentUpload');
      setAlert({
        isOpen: true,
        type: 'warning',
        title: 'No File Selected',
        message: 'Please select a file to upload.'
      });
      return;
    }

    const userId = user?.id || user?.uid || authUser?.uid;
    
    if (!userId) {
      console.error('No user ID found:', { user, authUser });
      setAlert({
        isOpen: true,
        type: 'error',
        title: 'Authentication Error',
        message: 'User not found. Please sign in again.'
      });
      return;
    }
    setUploadingDoc(documentType);

    try {
      const result = await uploadUserDocument(file, userId, documentType);
      
      if (result.error) {
        setAlert({
          isOpen: true,
          type: 'error',
          title: 'Upload Failed',
          message: `Failed to upload ${documentType}: ${result.error}`
        });
        setUploadingDoc(null);
        return;
      }

      // Update user profile with document URL
      const updateData = {
        [`documents.${documentType}.url`]: result.url,
        [`documents.${documentType}.uploadedAt`]: new Date().toISOString(),
        [`documents.${documentType}.verified`]: false // Reset verification on new upload
      };

      const { error } = await updateUserProfile(userId, updateData);
      
      if (error) {
        setAlert({
          isOpen: true,
          type: 'error',
          title: 'Save Failed',
          message: `Failed to save document: ${error}`
        });
      } else {
        // Refresh user profile
        if (refreshUserProfile) {
          await refreshUserProfile();
        }
        const result = await getUserById(userId);
        if (result.user) {
          setUser(result.user);
        }
        setAlert({
          isOpen: true,
          type: 'success',
          title: 'Upload Successful',
          message: 'Document uploaded successfully!'
        });
      }
    } catch (error) {
      console.error('Error uploading document:', error);
      setAlert({
        isOpen: true,
        type: 'error',
        title: 'Upload Failed',
        message: 'Failed to upload document. Please try again.'
      });
    } finally {
      setUploadingDoc(null);
    }
  };

  // Handle file input change
  const handleFileChange = (documentType, e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    
    // Validate file type
    if (!file.type.startsWith('image/')) {
      setAlert({
        isOpen: true,
        type: 'warning',
        title: 'Invalid File Type',
        message: 'Please upload an image file (JPG, PNG, etc.)'
      });
      e.target.value = ''; // Reset input
      return;
    }
    
    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setAlert({
        isOpen: true,
        type: 'warning',
        title: 'File Too Large',
        message: 'File size must be less than 5MB. Please choose a smaller file.'
      });
      e.target.value = ''; // Reset input
      return;
    }
    
    handleDocumentUpload(documentType, file);
  };

  // Get document verification status
  const getDocumentStatus = (documentType) => {
    const doc = user?.documents?.[documentType];
    if (!doc?.url) return 'missing';
    if (doc.verified) return 'verified';
    return 'pending';
  };

  // Check if all documents are uploaded
  const hasAllDocuments = () => {
    return user?.documents?.['license-front']?.url &&
           user?.documents?.['license-back']?.url &&
           user?.documents?.['aadhar-front']?.url;
  };

  // Check if all documents are verified
  const areAllDocumentsVerified = () => {
    return user?.documents?.['license-front']?.verified &&
           user?.documents?.['license-back']?.verified &&
           user?.documents?.['aadhar-front']?.verified;
  };

  if (pageLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 flex items-center justify-center">
        <Loading />
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Card className="p-8 text-center max-w-md">
          <h2 className="text-2xl font-semibold text-gray-900 mb-4">No User Data</h2>
          <p className="text-gray-600 mb-6">Unable to load user profile. Please sign in again.</p>
          <Button onClick={() => navigate('/login')} className="gradient-primary text-white">
            Go to Login
          </Button>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50">
      <Navbar />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8 lg:py-12">
        {/* Profile Header */}
        <div className="text-center mb-6 sm:mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 sm:w-24 sm:h-24 bg-gradient-primary rounded-full mb-3 sm:mb-4 shadow-soft">
            <FiUser className="w-10 h-10 sm:w-12 sm:h-12 text-white" />
          </div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 sm:mb-2">My Profile</h1>
          <p className="text-sm sm:text-base text-gray-600">Manage your account information</p>
        </div>

        {/* Profile Card */}
        <Card className="p-4 sm:p-6 lg:p-8 border-0 shadow-medium">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-4 sm:mb-6 gap-3">
            <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Personal Information</h2>
            {!isEditing && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setIsEditing(true)}
                className="w-full sm:w-auto"
              >
                <FiEdit2 className="mr-2" />
                Edit Profile
              </Button>
            )}
          </div>

          <div className="space-y-6">
            <div>
              <Input
                label="Full Name"
                type="text"
                name="displayName"
                value={formData.displayName || ''}
                onChange={handleChange}
                placeholder="Enter your full name"
                error={errors.displayName}
                disabled={!isEditing}
                icon={<FiUser className="w-5 h-5 text-gray-400" />}
              />
            </div>

            <div>
              <Input
                label="Email Address"
                type="email"
                name="email"
                value={formData.email || ''}
                disabled={true}
                placeholder="No email available"
                icon={<FiMail className="w-5 h-5 text-gray-400" />}
              />
              <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
            </div>

            <div>
              <Input
                label="Phone Number"
                type="tel"
                name="phone"
                value={formData.phone || ''}
                onChange={handleChange}
                placeholder="Enter your phone number"
                error={errors.phone}
                disabled={!isEditing}
                icon={<FiPhone className="w-5 h-5 text-gray-400" />}
                required
              />
            </div>

            <div>
              <Input
                label="Aadhar Number"
                type="text"
                name="aadharNumber"
                value={formData.aadharNumber || ''}
                onChange={handleChange}
                placeholder="Enter your 12-digit Aadhar number"
                error={errors.aadharNumber}
                disabled={!isEditing}
                icon={<FiShield className="w-5 h-5 text-gray-400" />}
                required
              />
            </div>

            <div>
              <Input
                label="Alternate Mobile Number"
                type="tel"
                name="alternatePhone"
                value={formData.alternatePhone || ''}
                onChange={handleChange}
                placeholder="Enter alternate phone number (optional)"
                error={errors.alternatePhone}
                disabled={!isEditing}
                icon={<FiPhone className="w-5 h-5 text-gray-400" />}
              />
            </div>

            <div>
              <Input
                label="WhatsApp Number"
                type="tel"
                name="whatsappNumber"
                value={formData.whatsappNumber || ''}
                onChange={handleChange}
                placeholder="Enter WhatsApp number (optional)"
                error={errors.whatsappNumber}
                disabled={!isEditing}
                icon={<FiPhone className="w-5 h-5 text-gray-400" />}
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Address <span className="text-red-500">*</span>
              </label>
              <textarea
                name="address"
                value={formData.address || ''}
                onChange={handleChange}
                placeholder="Enter your complete address"
                disabled={!isEditing}
                rows={3}
                className={`w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-colors ${
                  errors.address ? 'border-red-500' : 'border-gray-300'
                } ${!isEditing ? 'bg-gray-50 cursor-not-allowed' : 'bg-white'}`}
              />
              {errors.address && (
                <p className="mt-1 text-sm text-red-600">{errors.address}</p>
              )}
            </div>
          </div>

          {isEditing && (
            <div className="flex gap-3 mt-8 pt-6 border-t border-gray-200">
              <Button
                variant="outline"
                onClick={handleCancel}
                fullWidth
              >
                <FiX className="mr-2" />
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                isLoading={loading}
                fullWidth
                className="gradient-primary text-white"
              >
                <FiSave className="mr-2" />
                Save Changes
              </Button>
            </div>
          )}
        </Card>

        {/* Documents Section */}
        <Card className="p-4 sm:p-6 lg:p-8 border-0 shadow-medium mt-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">Identity Documents</h2>
              <p className="text-sm text-gray-600 mt-1">Upload your documents for verification</p>
            </div>
            {areAllDocumentsVerified() && (
              <div className="flex items-center gap-2 px-3 py-1.5 bg-green-100 text-green-700 rounded-full">
                <FiCheckCircle className="w-4 h-4" />
                <span className="text-sm font-medium">Verified</span>
              </div>
            )}
          </div>

          <div className="space-y-6">
            {/* Driver License */}
            <div>
              <h3 className="text-base font-medium text-gray-900 mb-4">Driving License</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* License Front */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Front Side <span className="text-red-500">*</span>
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                    {user?.documents?.['license-front']?.url ? (
                      <div className="relative">
                        <img
                          src={user.documents['license-front'].url}
                          alt="License Front"
                          className="w-full h-48 object-cover rounded-lg"
                        />
                        <div className="absolute top-2 right-2">
                          {user.documents['license-front'].verified ? (
                            <div className="bg-green-500 text-white rounded-full p-1.5">
                              <FiCheckCircle className="w-4 h-4" />
                            </div>
                          ) : (
                            <div className="bg-yellow-500 text-white rounded-full p-1.5">
                              <FiShield className="w-4 h-4" />
                            </div>
                          )}
                        </div>
                        <input
                          ref={licenseFrontRef}
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileChange('license-front', e)}
                          className="hidden"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => licenseFrontRef.current?.click()}
                          disabled={uploadingDoc === 'license-front'}
                          className="mt-2 w-full"
                        >
                          {uploadingDoc === 'license-front' ? (
                            <>Uploading...</>
                          ) : (
                            <>
                              <FiUpload className="mr-2" />
                              Replace
                            </>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <input
                          ref={licenseFrontRef}
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileChange('license-front', e)}
                          className="hidden"
                        />
                        <FiUpload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600 mb-3">Upload license front</p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => licenseFrontRef.current?.click()}
                          disabled={uploadingDoc === 'license-front'}
                        >
                          {uploadingDoc === 'license-front' ? 'Uploading...' : 'Choose File'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>

                {/* License Back */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Back Side <span className="text-red-500">*</span>
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                    {user?.documents?.['license-back']?.url ? (
                      <div className="relative">
                        <img
                          src={user.documents['license-back'].url}
                          alt="License Back"
                          className="w-full h-48 object-cover rounded-lg"
                        />
                        <div className="absolute top-2 right-2">
                          {user.documents['license-back'].verified ? (
                            <div className="bg-green-500 text-white rounded-full p-1.5">
                              <FiCheckCircle className="w-4 h-4" />
                            </div>
                          ) : (
                            <div className="bg-yellow-500 text-white rounded-full p-1.5">
                              <FiShield className="w-4 h-4" />
                            </div>
                          )}
                        </div>
                        <input
                          ref={licenseBackRef}
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileChange('license-back', e)}
                          className="hidden"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => licenseBackRef.current?.click()}
                          disabled={uploadingDoc === 'license-back'}
                          className="mt-2 w-full"
                        >
                          {uploadingDoc === 'license-back' ? (
                            <>Uploading...</>
                          ) : (
                            <>
                              <FiUpload className="mr-2" />
                              Replace
                            </>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <input
                          ref={licenseBackRef}
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileChange('license-back', e)}
                          className="hidden"
                        />
                        <FiUpload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600 mb-3">Upload license back</p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => licenseBackRef.current?.click()}
                          disabled={uploadingDoc === 'license-back'}
                        >
                          {uploadingDoc === 'license-back' ? 'Uploading...' : 'Choose File'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Aadhar Card */}
            <div>
              <h3 className="text-base font-medium text-gray-900 mb-4">Aadhar Card</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Aadhar Front */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Front Side <span className="text-red-500">*</span>
                  </label>
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4">
                    {user?.documents?.['aadhar-front']?.url ? (
                      <div className="relative">
                        <img
                          src={user.documents['aadhar-front'].url}
                          alt="Aadhar Front"
                          className="w-full h-48 object-cover rounded-lg"
                        />
                        <div className="absolute top-2 right-2">
                          {user.documents['aadhar-front'].verified ? (
                            <div className="bg-green-500 text-white rounded-full p-1.5">
                              <FiCheckCircle className="w-4 h-4" />
                            </div>
                          ) : (
                            <div className="bg-yellow-500 text-white rounded-full p-1.5">
                              <FiShield className="w-4 h-4" />
                            </div>
                          )}
                        </div>
                        <input
                          ref={aadharFrontRef}
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileChange('aadhar-front', e)}
                          className="hidden"
                        />
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => aadharFrontRef.current?.click()}
                          disabled={uploadingDoc === 'aadhar-front'}
                          className="mt-2 w-full"
                        >
                          {uploadingDoc === 'aadhar-front' ? (
                            <>Uploading...</>
                          ) : (
                            <>
                              <FiUpload className="mr-2" />
                              Replace
                            </>
                          )}
                        </Button>
                      </div>
                    ) : (
                      <div className="text-center py-8">
                        <input
                          ref={aadharFrontRef}
                          type="file"
                          accept="image/*"
                          onChange={(e) => handleFileChange('aadhar-front', e)}
                          className="hidden"
                        />
                        <FiUpload className="w-8 h-8 text-gray-400 mx-auto mb-2" />
                        <p className="text-sm text-gray-600 mb-3">Upload Aadhar front</p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => aadharFrontRef.current?.click()}
                          disabled={uploadingDoc === 'aadhar-front'}
                        >
                          {uploadingDoc === 'aadhar-front' ? 'Uploading...' : 'Choose File'}
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Status Message */}
            {!hasAllDocuments() && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <FiShield className="w-5 h-5 text-amber-600 mt-0.5" />
                  <div>
                    <p className="text-sm font-medium text-amber-900">Documents Required</p>
                    <p className="text-sm text-amber-700 mt-1">
                      Please upload all required documents to proceed with bookings.
                    </p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Additional Info */}
        <Card className="p-6 border-0 shadow-soft mt-6">
          <h3 className="font-semibold text-gray-900 mb-4">Account Details</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-600">User ID</span>
              <span className="font-medium text-gray-900 font-mono text-xs">
                {user.uid || user.email || 'N/A'}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Account Status</span>
              <span className="px-2 py-1 bg-green-100 text-green-700 rounded text-xs font-medium">
                Active
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-600">Member Since</span>
              <span className="font-medium text-gray-900">
                {user.createdAt ? 
                  (user.createdAt.seconds ? 
                    new Date(user.createdAt.seconds * 1000).toLocaleDateString('en-IN', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    }) :
                    new Date(user.createdAt).toLocaleDateString('en-IN', { 
                      year: 'numeric', 
                      month: 'long', 
                      day: 'numeric' 
                    })
                  ) : 
                  new Date().toLocaleDateString('en-IN', { 
                    year: 'numeric', 
                    month: 'long', 
                    day: 'numeric' 
                  })
                }
              </span>
            </div>
          </div>
        </Card>
      </div>

      {/* Alert Modal */}
      <AlertModal
        isOpen={alert.isOpen}
        onClose={() => setAlert({ ...alert, isOpen: false })}
        type={alert.type}
        title={alert.title}
        message={alert.message}
      />
    </div>
  );
};

export default Profile;

